import React, { useState, useCallback } from 'react'
import { ImageComponent } from 'src/views/components'
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai'

const ProductImageCarousel = ({
  images = [],
  alt = '',
  testId = 'qa-plp-product-image',
  loadingType = 'lazy',
  onImageClick,
}) => {
  const [index, setIndex] = useState(0)

  /**
   * Returns true if the event's client coordinates are inside any carousel control.
   * Defensive: returns false on errors or when running server-side.
   */
  const isEventInsideCarouselControl = e => {
    try {
      if (
        typeof document === 'undefined' ||
        !e ||
        typeof e.clientX !== 'number'
      )
        return false
      const cx = e.clientX
      const cy = e.clientY
      const controls = document.querySelectorAll(
        '.plp-carousel-wrapper .carousel-control'
      )
      for (let i = 0; i < controls.length; i++) {
        const r = controls[i].getBoundingClientRect()
        if (cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom)
          return true
      }
    } catch (err) {
      /* ignore and allow normal behavior */
    }
    return false
  }

  if (!Array.isArray(images) || images.length === 0) {
    return (
      <ImageComponent
        testId={testId}
        className="plp-product-image"
        src=""
        alt={alt}
        plpImprovementClass={true}
        optimizeImage={true}
        loadingType={loadingType}
      />
    )
  }

  const total = images.length
  const hasMultiple = total > 1
  const isPrevDisabled = index === 0
  const isNextDisabled = index === total - 1

  const handlePreventDefaultStopPropagation = e => {
    e.preventDefault()
    e.stopPropagation()
  }

  const goPrev = useCallback(
    e => {
      handlePreventDefaultStopPropagation(e)
      if (isPrevDisabled) return
      setIndex(prev => (prev > 0 ? prev - 1 : prev))
    },
    [isPrevDisabled]
  )

  const goNext = useCallback(
    e => {
      handlePreventDefaultStopPropagation(e)
      if (isNextDisabled) return
      setIndex(prev => (prev < total - 1 ? prev + 1 : prev))
    },
    [isNextDisabled, total]
  )

  const handleKey = e => {
    if (!hasMultiple) return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (!isPrevDisabled) goPrev(e)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      if (!isNextDisabled) goNext(e)
    }
  }

  return (
    <div
      className={`plp-carousel-wrapper ${hasMultiple ? 'has-multiple' : ''}`}
      tabIndex={hasMultiple ? 0 : -1}
      onKeyDown={handleKey}
      aria-roledescription={hasMultiple ? 'carousel' : undefined}
      aria-label={alt}
      data-testid="qa-plp-carousel-wrapper">
      <div
        className="carousel-image-click-layer"
        onClick={e => {
          // ignore clicks that originate on/through carousel controls (JS-only hit-test)
          if (isEventInsideCarouselControl(e)) {
            handlePreventDefaultStopPropagation(e)
            return
          }
          if (onImageClick) onImageClick(e)
        }}>
        <ImageComponent
          testId={testId}
          className="plp-product-image"
          src={images[index]}
          alt={alt}
          plpImprovementClass={true}
          optimizeImage={true}
          loadingType={loadingType}
        />
      </div>
      {hasMultiple && (
        <>
          <button
            type="button"
            className={`carousel-control carousel-prev ${isPrevDisabled ? 'disabled' : ''}`}
            onClick={e => {
              if (isPrevDisabled) {
                handlePreventDefaultStopPropagation(e)
                return
              }
              goPrev(e)
            }}
            onKeyDown={e => {
              if (isPrevDisabled && (e.key === 'Enter' || e.key === ' ')) {
                handlePreventDefaultStopPropagation(e)
                return
              }
            }}
            onMouseDown={e => {
              handlePreventDefaultStopPropagation(e)
            }}
            aria-label="Previous image"
            aria-disabled={isPrevDisabled}
            tabIndex={isPrevDisabled ? -1 : 0}
            data-testid="qa-plp-carousel-prev">
            <AiOutlineLeft size={20} color="#252525" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`carousel-control carousel-next ${isNextDisabled ? 'disabled' : ''}`}
            onClick={e => {
              if (isNextDisabled) {
                handlePreventDefaultStopPropagation(e)
                return
              }
              goNext(e)
            }}
            onKeyDown={e => {
              if (isNextDisabled && (e.key === 'Enter' || e.key === ' ')) {
                handlePreventDefaultStopPropagation(e)
                return
              }
            }}
            onMouseDown={e => {
              handlePreventDefaultStopPropagation(e)
            }}
            aria-label="Next image"
            aria-disabled={isNextDisabled}
            tabIndex={isNextDisabled ? -1 : 0}
            data-testid="qa-plp-carousel-next">
            <AiOutlineRight size={20} color="#252525" aria-hidden="true" />
          </button>
          <div
            className="carousel-dash-container"
            role="tablist"
            aria-label="Image selection">
            {images.map((img, i) => (
              <button
                key={img + i}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Show image ${i + 1} of ${total}`}
                className={`carousel-dash ${i === index ? 'active' : ''}`}
                onClick={e => {
                  handlePreventDefaultStopPropagation(e)
                  setIndex(i)
                }}
                onMouseDown={e => {
                  handlePreventDefaultStopPropagation(e)
                }}
                data-testid={`qa-plp-carousel-indicator-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default ProductImageCarousel
